import { useNavigate } from 'react-router'
import { useIsMobile } from '../hooks/useMediaQuery'
import { colors, fontFamily, spacing, fontSize, radius, shadow } from '../styles/theme'

interface Tier {
  name: string
  price: string
  period: string
  allowance: string
  overage: string
  description: string
  highlight: boolean
  features: string[]
}

/** Kept in step with V045/V073 (billing_rate) and lib/src/Services/Billing/PlanFeatures.cs. */
const TIERS: Tier[] = [
  {
    name: 'Standard',
    price: '$45',
    period: 'per month',
    allowance: '5,000 pages included',
    overage: 'then $0.0105 per page',
    description: 'Everything you need to generate documents from your app.',
    highlight: false,
    features: [
      'Up to 8 documents rendering at once',
      'Word, HTML & Markdown templates',
      'Visual editor with drag-and-drop blocks & live preview',
      'Starter layouts and a team block library',
      'Localization: translations per locale, locale-aware dates & numbers',
      'Every output format: PDF, DOCX, XLSX, PPTX, ODF, HTML',
      'Test & Live workspaces',
      'Promote a version to Live, now or on a schedule',
      'Full version history, compare & copy',
      'REST API, SDKs, MCP server & iframe embedding',
      'AI template generation & field mapping',
      'Document analytics & view tracking',
      'Import from Carbone, Docmosis & Handlebars',
      'MFA and role-based access',
    ],
  },
  {
    name: 'Enhanced',
    price: '$65',
    period: 'per month',
    allowance: '10,000 pages included',
    overage: 'then $0.0089 per page',
    description: 'For teams generating documents in volume.',
    highlight: true,
    features: [
      'Everything in Standard, plus:',
      'Up to 16 documents rendering at once',
      'Batch rendering from CSV & Excel',
      'Async rendering',
      'Signed outbound webhooks',
      'Delivery to Google Drive, Dropbox, OneDrive & SharePoint',
      'Folder-scoped API keys',
      'Shareable preview links',
      'Usage analytics',
    ],
  },
  {
    name: 'Ultimate',
    price: '$85',
    period: 'per month',
    allowance: '15,000 pages included',
    overage: 'then $0.0076 per page',
    description: 'For data-driven pipelines and regulated environments.',
    highlight: false,
    features: [
      'Everything in Enhanced, plus:',
      'Up to 32 documents rendering at once',
      'Database connectors: Postgres, MySQL, SQL Server, MongoDB',
      'Warehouse connectors: BigQuery, Snowflake, Redshift',
      'Inbound connectors that trigger renders',
      'Retention policies & legal hold',
      'Audit log & export',
      'E-signature',
      'Translate with AI (paid per use, priced before you confirm)',
      'Error-rate alerting',
    ],
  },
]

const FAQ = [
  {
    q: 'What counts as a page?',
    a: 'Every page of a successfully rendered document, in any output format. Failed renders are not counted. Renders made with a Test key count the same as Live ones.',
  },
  {
    q: 'What happens if I go over my included pages?',
    a: 'By default, renders continue and pages beyond your allowance are billed at your plan\'s per-page rate, up to a monthly overage limit of $25 that you can raise, lower or remove on the Billing page. Renders that would go past the limit are refused, so the overage on a bill never exceeds it. You can also turn overage billing off, and renders stop at your included pages. The plans are priced so that moving up a tier always costs less than running well past your current one.',
  },
  {
    q: 'How does the free trial work?',
    a: 'Every new account starts a 30-day trial of Standard. A card is required to start, but nothing is charged during the trial — billing begins with the first full calendar month after it ends.',
  },
  {
    q: 'What does "documents rendering at once" mean?',
    a: 'It is how many documents your team can have rendering at the same moment across all of our servers — 8 on Standard, 16 on Enhanced, 32 on Ultimate. Requests beyond that are answered with HTTP 429 and a Retry-After header, so your client simply retries a second later; nothing is lost or billed twice. Batch requests wait for a free slot automatically. Need more for a large batch run? Contact us for a negotiated limit.',
  },
  {
    q: 'Can I change plans?',
    a: 'Yes, from the Billing page at any time. Removing your card never changes your plan; rendering simply pauses until a card is back on file.',
  },
  {
    q: 'What is the difference between Test and Live?',
    a: 'Every plan includes both. You iterate on templates in Test, where a Test API key can render any version (watermarked). When a version is ready you promote it to Live, and Live keys render only that version until you promote another or demote it.',
  },
  {
    q: 'How is Translate with AI charged?',
    a: 'Translate with AI is an Ultimate feature, paid per use. Before anything runs you choose the languages and see the exact price for each and the total (at least $0.50); you are charged that quote and never more, only for languages that produced drafts, on your next invoice.',
  },
  {
    q: 'We render more than Ultimate covers. Can we get a better rate?',
    a: 'Yes. Request custom pricing from the Billing page and we will set a negotiated per-page rate for your team, on whichever plan you are on.',
  },
]

function Check() {
  return <span style={{ color: colors.success, fontWeight: 700, marginRight: spacing.sm }}>✓</span>
}

export default function PricingPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  return (
    <div style={{ fontFamily: fontFamily.sans }}>
      {/* Hero */}
      <section style={{ textAlign: 'center', padding: isMobile ? `${spacing.xxl} ${spacing.lg}` : `${spacing.xxl} ${spacing.lg} ${spacing.xl}` }}>
        <h1 style={{ fontSize: isMobile ? '1.75rem' : '2.5rem', fontWeight: 900, color: colors.text, margin: `0 0 ${spacing.sm}`, letterSpacing: '-0.5px' }}>
          Simple, transparent pricing
        </h1>
        <p style={{ fontSize: fontSize.lg, color: colors.textSecondary, margin: 0 }}>
          Every plan starts with a 30-day free trial. Billed per page, never cut off.
        </p>
      </section>

      {/* Tiers */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${spacing.lg} ${spacing.xxl}` }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: spacing.lg,
          alignItems: 'start',
        }}>
          {TIERS.map(tier => (
            <div
              key={tier.name}
              style={{
                background: colors.surface,
                border: `2px solid ${tier.highlight ? colors.primary : colors.border}`,
                borderRadius: radius.lg,
                padding: spacing.xl,
                boxShadow: tier.highlight ? shadow.lg : shadow.sm,
                position: 'relative',
              }}
            >
              {tier.highlight && (
                <div style={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: colors.primary,
                  color: colors.onPrimary,
                  fontSize: fontSize.xs,
                  fontWeight: 700,
                  padding: '3px 12px',
                  borderRadius: radius.full,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  whiteSpace: 'nowrap',
                }}>
                  Most popular
                </div>
              )}

              <div style={{ marginBottom: spacing.lg }}>
                <div style={{ fontSize: fontSize.base, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: spacing.xs }}>{tier.name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: spacing.xs, marginBottom: spacing.sm }}>
                  <span style={{ fontSize: '2.25rem', fontWeight: 900, color: colors.text, letterSpacing: '-1px' }}>{tier.price}</span>
                  <span style={{ fontSize: fontSize.sm, color: colors.textMuted }}>{tier.period}</span>
                </div>
                <div style={{ fontSize: fontSize.base, fontWeight: 600, color: colors.text }}>{tier.allowance}</div>
                <div style={{ fontSize: fontSize.sm, color: colors.textMuted, marginBottom: spacing.sm }}>{tier.overage}</div>
                <div style={{ fontSize: fontSize.base, color: colors.textSecondary, lineHeight: 1.5 }}>{tier.description}</div>
              </div>

              <button
                onClick={() => navigate('/register')}
                style={{
                  width: '100%',
                  padding: `10px ${spacing.md}`,
                  background: tier.highlight ? colors.primary : 'transparent',
                  color: tier.highlight ? colors.onPrimary : colors.primary,
                  border: `2px solid ${colors.primary}`,
                  borderRadius: radius.full,
                  fontSize: fontSize.base,
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginBottom: spacing.lg,
                }}
              >
                Start free trial
              </button>

              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: spacing.lg }}>
                {tier.features.map(f => f.endsWith(':') ? (
                  <div key={f} style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, marginBottom: spacing.sm }}>{f}</div>
                ) : (
                  <div key={f} style={{ fontSize: fontSize.base, color: colors.text, marginBottom: spacing.sm, display: 'flex', alignItems: 'flex-start' }}>
                    <Check />{f}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section style={{
        maxWidth: 720,
        margin: '0 auto',
        padding: `0 ${spacing.lg} ${spacing.xxl}`,
      }}>
        <h2 style={{ textAlign: 'center', fontSize: isMobile ? fontSize.xl : '1.75rem', fontWeight: 800, color: colors.text, margin: `0 0 ${spacing.xl}` }}>
          Frequently asked questions
        </h2>
        {FAQ.map(({ q, a }) => (
          <div key={q} style={{
            borderBottom: `1px solid ${colors.border}`,
            padding: `${spacing.lg} 0`,
          }}>
            <div style={{ fontWeight: 700, fontSize: fontSize.md, color: colors.text, marginBottom: spacing.sm }}>{q}</div>
            <div style={{ fontSize: fontSize.base, color: colors.textSecondary, lineHeight: 1.7 }}>{a}</div>
          </div>
        ))}
      </section>
    </div>
  )
}
